import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  ElementRef,
  HostBinding,
  Input,
  QueryList,
  Renderer2,
  ViewEncapsulation,
} from "@angular/core";

export type LoadingVariant = "circle-spin" | "shimmer" | "spinner" | "typing";

/**
 * Vattenfall UI `ui-loading` is a shimmering indicator of progress and activity.
 *
 * The loading element inherits the width and height of either its host element – `<ui-loading>`, or any element provided as content children to the loading element that has an `#loader` template reference – `<h1 #loader>`. If the loading element contains a single text node then the loader will be styled in a fashion which more closely resembles the text it is supposed to represent.
 *
 * The spinner will span the entire height and width of the host element if it is not empty, but none of its child elements has an `loader` template reference.
 *
 * The circle-spin and the typing loading indicator does not accept `loader` child elements.
 */
@Component({
  selector: "ui-loading, [ui-loading]",
  templateUrl: "./loading.component.html",
  styleUrls: ["./loading.component.scss"],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoadingComponent implements AfterContentChecked {
  /**
   * The variants to show this component in
   */
  @HostBinding("attr.variant")
  @Input()
  public variant: LoadingVariant = "shimmer";

  /**
   * Whether the component loading state is activated
   */
  @HostBinding("attr.loading")
  @Input()
  public loading = true;

  /**
   * Holds all elements passed as content children with `#loader` template reference ID.
   */
  @ContentChildren("loader", { descendants: true })
  private queryList!: QueryList<ElementRef<HTMLElement>>;

  constructor(private elementRef: ElementRef<HTMLElement>, private renderer: Renderer2) {}

  ngAfterContentChecked() {
    // The provided content children has elements with `loader` references
    if (this.queryList.length && this.variant !== "typing" && this.variant !== "circle-spin")
      // Add class names to content children
      this.queryList.forEach(this.addClass);
    // Add class names to host element
    else this.addClass(this.elementRef);
  }

  /**
   * Adds class names to host element or nested content children depending on the display type of that element
   */
  private addClass = (elementRef: ElementRef<HTMLElement>): void => {
    this.renderer.addClass(elementRef.nativeElement, "loader");
  };
}
